import assert from 'http-assert-plus';
import axios from 'axios';
import AuthResumeSessionQuery from '@/graphql/queries/AuthResumeSession.gql';
import base64 from 'base-64';
import CognitoAuth from '@src/lib/cognito';
import Vue from 'vue';

const IS_PRODUCTION = window.location.origin === 'https://admin.reallinks.io';
let authToken = null;
const localStorage = 'localStorage' in global && global.localStorage ? global.localStorage : null;
const sessionStorage = 'sessionStorage' in global && global.sessionStorage ? global.sessionStorage : null;

export const cognitoAuth = new CognitoAuth({
  UserPoolId: IS_PRODUCTION ? 'eu-west-2_ATuXU8Lju' : 'eu-west-2_hJxHx9fC2', // admin vs dev
  ClientId: IS_PRODUCTION ? '619svk98go716aua2u7o2avagb' : '52ksid67a5kbklrq5vmji9eqp0', // client
  AppWebDomain: `real-links-${IS_PRODUCTION ? 'admin' : 'dev'}.auth.eu-west-2.amazoncognito.com`, // admin vs dev
  TokenScopesArray: [ 'openid', 'email', 'profile' ],
  RedirectUriSignIn: `${window.location.origin}/login/confirm`,
  RedirectUriSignOut: `${window.location.origin}/logout/confirm`,
  IdentityProvider: 'real-links-google-ad',
  AdvancedSecurityDataCollectionFlag: true,
}, {
  onSuccess(result) {
    Vue.$log.debug('Cognito userhandler success', result);
    if (result && result.idToken && result.idToken.getJwtToken()) {
      authToken = result.idToken;
    }
  },
  onFailure(err) {
    Vue.$log.error('Cognito userhandler error', err);
  },
});

// Life-saving line that means we use the code grant, and therefore get a refresh token!
cognitoAuth.useCodeGrantFlow();
// All hail the refresh token!

const adminAxios = axios.create({
  baseURL: IS_PRODUCTION
    ? 'https://70n1vfow5h.execute-api.eu-west-2.amazonaws.com/prod'
    : 'https://asl8jo6844.execute-api.eu-west-2.amazonaws.com/dev',
  maxRedirects: 0,
  validateStatus: status => status === 200,
});
adminAxios.interceptors.request.use(async config => {
  if (!authToken) {
    authToken = cognitoAuth.getCachedSession().getIdToken();
  }

  // If no authToken, or the current authToken is invalid, get a new one
  if (authToken && Math.floor(Date.now() / 1000) > authToken.getExpiration()) {
    const { idToken } = await cognitoAuth.getSession();
    authToken = idToken;

    // If we still can't get anywhere, fake a 401 error rather than wasting a request
    if (!authToken || !cognitoAuth.isUserSignedIn()) {
      return {
        Status: 401,
        Error: {
          Type: 'UnauthorizedError',
          Message: 'You are not signed-in',
        },
      };
    }
  }

  if (authToken && authToken.getJwtToken()) {
    config.headers = config.headers || {};
    config.headers.Authorization = authToken.getJwtToken();
  }

  return config;
});
adminAxios.interceptors.response.use(({ data }) => data, err => {
  if (err.response && err.response.data && err.response.data.Status) {
    return err.response.data;
  }

  return Promise.reject(err);
});

export async function adminAPI({ ServiceName, FunctionName, InvocationType, Payload }, { validateStatus } = {}) {
  const data = await adminAxios.post('/api', { ServiceName, FunctionName, InvocationType, Payload });

  if (data.Error) {
    // Transform the API error into a native Error type
    const { Type, Code, Message } = data.Error;
    const err = new Error(Message);
    err.name = Type;
    err.code = Code;
    data.Error = err;
  }

  assert(!validateStatus || (data && data.Status && data.Status === validateStatus), data.Error || {});
  return { status: data.Status, err: data.Error, result: data.Result };
}

export function getCognitoUser() {
  const idToken = cognitoAuth.getCachedSession().getIdToken();
  if (idToken && idToken.getJwtToken()) {
    const [ , encoded ] = idToken.getJwtToken().split('.');
    const { given_name: firstName, family_name: lastName, email: emailAddress } = JSON.parse(base64.decode(encoded));
    const isLoggedIn = cognitoAuth.isUserSignedIn();
    const isValid = Math.floor(new Date() / 1000) < idToken.getExpiration();
    return { firstName, lastName, emailAddress, isLoggedIn, isValid };
  } else {
    return null;
  }
}

export function isJwtValid(token) {
  const [ , jwtEncoded ] = (typeof token === 'string' ? token : '').split('.');
  const { exp } = JSON.parse('atob' in global && typeof global.atob === 'function' ? global.atob(jwtEncoded) : null);
  return exp ? Math.floor(Date.now() / 1000) < exp : false;
}

export function getCurrentCompanyID() {
  return sessionStorage ? JSON.parse(sessionStorage.getItem('currentCompanyID')) : null;
}

export async function companyStartSession(inst, { companyID }) {
  let primaryDomain = localStorage ? JSON.parse(localStorage.getItem(`primaryDomain:${companyID}`)) : null;
  let token = localStorage ? JSON.parse(localStorage.getItem(`token:${companyID}`)) : null;

  if (!primaryDomain || !token || !isJwtValid(token)) {
    const { result } = await adminAPI({
      ServiceName: 'admin',
      FunctionName: 'companyLogin',
      Payload: { companyID },
    }, {
      validateStatus: 200,
    });

    assert(result && typeof result.token === 'string', new Error('Failed to get token from admin-companyLogin'));
    ({ token, primaryDomain } = result);

    localStorage && localStorage.setItem(`token:${companyID}`, JSON.stringify(token));
    localStorage && localStorage.setItem(`primaryDomain:${companyID}`, JSON.stringify(primaryDomain));
  }

  sessionStorage && sessionStorage.setItem('currentCompanyID', JSON.stringify(companyID));
  sessionStorage && sessionStorage.setItem('hostname', JSON.stringify(primaryDomain));
  sessionStorage && sessionStorage.setItem('token', JSON.stringify(token));
  await inst.$apollo.query({ query: AuthResumeSessionQuery });
  return true;
}

export async function companyEndSession(inst, { companyID }) {
  const token = localStorage ? JSON.parse(localStorage.getItem(`token:${companyID}`)) : null;
  if (typeof token !== 'string' || !token.length) {
    Vue.$log.error(new Error('Failed to get token from localStorage'));
    return false;
  }

  localStorage && localStorage.removeItem(`token:${companyID}`);
  localStorage && localStorage.removeItem(`primaryDomain:${companyID}`);
  sessionStorage && sessionStorage.removeItem('currentCompanyID');
  sessionStorage && sessionStorage.removeItem('hostname');
  sessionStorage && sessionStorage.removeItem('token');

  try {
    const { defaultClient } = inst.$apollo.provider;
    defaultClient.cache.reset();
  } catch (err) {
    inst.$log.error('Error clearing apolloClient cache', err);
  }

  await adminAPI({
    ServiceName: 'admin',
    FunctionName: 'companyLogout',
    Payload: { companyID, token },
  }, {
    validateStatus: 200,
  }).catch(err => {
    inst.$log.error('Failed to logout with admin-companyLogout', err);
  });

  return true;
}

export function companySigninUrl(inst, { companyID }) {
  const hostname = localStorage ? JSON.parse(localStorage.getItem(`primaryDomain:${companyID}`)) : null;
  const token = localStorage ? JSON.parse(localStorage.getItem(`token:${companyID}`)) : null;
  return hostname && token ? `https://${hostname}/login/admin#token=${token}` : null;
}



// WEBPACK FOOTER //
// ./src/lib/admin.js